import { matchEventGuest, requestPhotoUploadUrls } from '@jq4race/api-client';
import { useTranslation } from '@jq4race/i18n';
import type { BatchCheckInItem, EventGuestResponse, EventPhotoPolicy } from '@jq4race/shared';
import { PHOTO_ANGLES } from '@jq4race/shared';
import {
  Badge,
  Button,
  Card,
  CardContent,
  Input,
  Label,
  Tabs,
  TabsList,
  TabsTrigger,
} from '@jq4race/ui';
import { AlertTriangle, Search, UserCheck, UserX } from 'lucide-react';
import { useCallback, useState } from 'react';
import { CameraCapture } from '../../../checkin/ui/components/CameraCapture';
import { IncidentPhotos } from '../../../checkin/ui/components/IncidentPhotos';
import { PhotoCaptureGrid } from '../../../checkin/ui/components/PhotoCaptureGrid';
import { usePhotoUpload } from '../../../checkin/ui/hooks/usePhotoUpload';

const E164 = /^\+[1-9]\d{6,14}$/;

type LookupState = 'idle' | 'loading' | 'done' | 'error';

function noop() {
  // intentionally empty — allPhotosDone is derived inside usePhotoUpload
}

interface FlashMatchFormProps {
  eventId: string;
  disabled?: boolean;
  /**
   * The selected event's angle-photo capture policy (event-photo-capture
   * Slice B). `required_4` mandates 4 one-per-angle photos before the
   * vehicle can be added; `optional` allows 0-4; `deferred` (or undefined —
   * e.g. events created before this feature) renders no angle-capture UI at
   * all, preserving pre-existing Flash behavior. Incident (angle-less)
   * photos (event-photo-capture Slice C) are wired independently of this
   * prop — available regardless of photoPolicy, 0..20, never gates "Add to
   * batch".
   */
  photoPolicy?: EventPhotoPolicy;
  /**
   * QA-oct I9 - parking zone field, driven by the location's check-in config
   * (`parking_zone`). Undefined = the location does not expose the field, so no
   * input renders (legacy behavior). `required` mirrors the config flag; the
   * server enforces it too (BatchCheckInUseCase).
   */
  zoneField?: { required: boolean };
  onAddToBatch: (item: BatchCheckInItem) => void;
}

function splitName(name: string): { firstName: string; lastName: string } {
  const trimmed = name.trim();
  const spaceIndex = trimmed.indexOf(' ');
  if (spaceIndex === -1) return { firstName: trimmed, lastName: '' };
  return { firstName: trimmed.slice(0, spaceIndex), lastName: trimmed.slice(spaceIndex + 1) };
}

/**
 * FlashMatchForm — the repeated "arrival" unit of Flash mode (R2.3/R2.4/R4.6):
 * plate (+ optional name) lookup against the event's guest list, then a
 * minimal add-to-batch form. Matched/ambiguous/walk-in are all surfaced but
 * NEVER auto-stamped here (stamping is a separate, not-yet-wired backend
 * concern) — this component only shapes a BatchCheckInItem for the queue.
 */
export function FlashMatchForm({
  eventId,
  disabled,
  photoPolicy,
  zoneField,
  onAddToBatch,
}: FlashMatchFormProps) {
  const { t } = useTranslation('flash');
  const [plate, setPlate] = useState('');
  const [lookupName, setLookupName] = useState('');
  const [lookupState, setLookupState] = useState<LookupState>('idle');
  const [lookupError, setLookupError] = useState<string | null>(null);
  const [matched, setMatched] = useState(false);
  const [ambiguous, setAmbiguous] = useState(false);
  const [candidates, setCandidates] = useState<EventGuestResponse[]>([]);
  const [selectedCandidateId, setSelectedCandidateId] = useState<string | null>(null);

  const [ticketType, setTicketType] = useState<'sms' | 'qr'>('sms');
  const [phone, setPhone] = useState('');
  const [qrTag, setQrTag] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [parkingZone, setParkingZone] = useState('');
  const [formError, setFormError] = useState<string | null>(null);

  // ── Angle photo capture (D1/D2, event-photo-capture Slice B) ───────────────
  // Hidden entirely under 'deferred' (or when the event predates this feature
  // and photoPolicy is undefined). Under 'required_4'/'optional' it reuses the
  // exact same hook/components as single check-in — capture-at-add (D1): the
  // upload fires as soon as a photo is captured, not deferred to batch submit,
  // so the S3 object persists past the 15-min presigned TTL even if the
  // operator queues several arrivals before submitting the batch.
  const showAngleCapture = photoPolicy === 'required_4' || photoPolicy === 'optional';
  const photoUpload = usePhotoUpload({ angles: PHOTO_ANGLES, requestUrls: requestPhotoUploadUrls });
  const angleCaptureIncomplete = photoPolicy === 'required_4' && !photoUpload.allPhotosDone;

  // ── Incident (angle-less) photo capture (event-photo-capture Slice C) ──────
  // Independent of photoPolicy/showAngleCapture above — available for every
  // vehicle regardless of the event's angle policy, 0..20, never blocks "Add
  // to batch". `IncidentPhotos` is a fully self-contained component (owns its
  // own upload orchestration, mirrors CheckinPage's usage) with no exposed
  // reset method, so it is force-remounted via `incidentResetKey` after each
  // successful add — the same effect `photoUpload.reset()` achieves for the
  // angle grid via its hook API.
  const [incidentPhotoKeys, setIncidentPhotoKeys] = useState<string[]>([]);
  const [incidentResetKey, setIncidentResetKey] = useState(0);
  const handleIncidentKeysChange = useCallback((keys: string[]) => {
    setIncidentPhotoKeys(keys);
  }, []);

  function resetForArrival() {
    setPlate('');
    setLookupName('');
    setLookupState('idle');
    setLookupError(null);
    setMatched(false);
    setAmbiguous(false);
    setCandidates([]);
    setSelectedCandidateId(null);
    setPhone('');
    setQrTag('');
    setFirstName('');
    setLastName('');
    setParkingZone('');
    setFormError(null);
    photoUpload.reset();
    setIncidentPhotoKeys([]);
    setIncidentResetKey((k) => k + 1);
  }

  async function handleLookup() {
    const trimmedPlate = plate.trim();
    if (!trimmedPlate) return;

    setLookupState('loading');
    setLookupError(null);
    // Clear any match-derived state from a PREVIOUS lookup before firing a new
    // one, so a match on plate A never leaks into plate B's entry when B comes
    // back ambiguous/no-match (regression fix — S4-UI review finding 1).
    setMatched(false);
    setAmbiguous(false);
    setCandidates([]);
    setSelectedCandidateId(null);
    setFirstName('');
    setLastName('');
    try {
      const result = await matchEventGuest(eventId, {
        plate: trimmedPlate,
        name: lookupName.trim() || undefined,
      });
      setMatched(result.matched);
      setAmbiguous(result.ambiguous);
      setCandidates(result.candidates);
      setLookupState('done');

      const onlyCandidate =
        result.matched && result.candidates.length === 1 ? result.candidates[0] : undefined;
      if (onlyCandidate) {
        const { firstName: fn, lastName: ln } = splitName(onlyCandidate.name);
        setFirstName(fn);
        setLastName(ln);
        setSelectedCandidateId(onlyCandidate.id);
      }
    } catch {
      setLookupState('error');
      setLookupError(t('matchForm.errors.lookupFailed'));
    }
  }

  function handleSelectCandidate(candidate: EventGuestResponse) {
    setSelectedCandidateId(candidate.id);
    const { firstName: fn, lastName: ln } = splitName(candidate.name);
    setFirstName(fn);
    setLastName(ln);
  }

  function handleAdd() {
    const trimmedPlate = plate.trim().toUpperCase();
    if (!trimmedPlate) {
      setFormError(t('matchForm.errors.plateRequired'));
      return;
    }
    if (ticketType === 'qr' && !qrTag.trim()) {
      setFormError(t('matchForm.errors.qrTagRequired'));
      return;
    }
    if (ticketType === 'sms' && !E164.test(phone.trim())) {
      setFormError(t('matchForm.errors.phoneInvalid'));
      return;
    }
    if (zoneField?.required && !parkingZone.trim()) {
      setFormError(t('matchForm.errors.zoneRequired'));
      return;
    }
    if (angleCaptureIncomplete) {
      setFormError(t('matchForm.errors.photosIncomplete'));
      return;
    }

    onAddToBatch({
      plate: trimmedPlate,
      ticketType,
      phone: ticketType === 'sms' ? phone.trim() : undefined,
      qrTag: ticketType === 'qr' ? qrTag.trim() : undefined,
      firstName: firstName.trim() || undefined,
      lastName: lastName.trim() || undefined,
      parkingZone: zoneField ? parkingZone.trim() || undefined : undefined,
      photoKeys:
        showAngleCapture && photoUpload.photoKeys.length > 0 ? photoUpload.photoKeys : undefined,
      incidentPhotoKeys: incidentPhotoKeys.length > 0 ? incidentPhotoKeys : undefined,
    });
    resetForArrival();
  }

  return (
    <Card>
      <CardContent className="space-y-4 pt-6">
        {/* ── Plate + name lookup ── */}
        <div className="grid gap-2">
          <Label htmlFor="flash-plate">{t('matchForm.plateLabel')}</Label>
          <Input
            id="flash-plate"
            autoComplete="off"
            autoCapitalize="characters"
            placeholder={t('matchForm.platePlaceholder')}
            className="h-14 text-[16px] font-semibold tracking-widest uppercase"
            value={plate}
            disabled={disabled}
            onChange={(e) => setPlate(e.target.value)}
          />
        </div>

        <div className="grid gap-2">
          <Label htmlFor="flash-name">{t('matchForm.nameLabel')}</Label>
          <Input
            id="flash-name"
            autoComplete="off"
            placeholder={t('matchForm.namePlaceholder')}
            className="h-12"
            value={lookupName}
            disabled={disabled}
            onChange={(e) => setLookupName(e.target.value)}
          />
        </div>

        <Button
          type="button"
          variant="secondary"
          size="touch"
          className="w-full"
          disabled={disabled || !plate.trim() || lookupState === 'loading'}
          onClick={() => void handleLookup()}
        >
          <Search className="size-4" strokeWidth={1.5} />
          {lookupState === 'loading' ? t('matchForm.lookingUp') : t('matchForm.lookUpGuest')}
        </Button>

        {/* ── Lookup result ── */}
        {lookupState === 'error' && lookupError && (
          <div
            role="alert"
            className="flex items-start gap-2 rounded-sm border border-destructive/60 bg-destructive/10 px-3 py-3 text-[13px] text-foreground"
          >
            <AlertTriangle className="mt-0.5 size-4 shrink-0 text-destructive" />
            <span>{lookupError}</span>
          </div>
        )}

        {lookupState === 'done' && matched && !ambiguous && (
          <div className="flex items-center gap-2 rounded-sm border border-border bg-secondary px-3 py-2 text-[13px]">
            <UserCheck className="size-4 shrink-0 text-jq-success" strokeWidth={1.5} />
            <span>
              {t('matchForm.matchedGuestLabel')} <strong>{candidates[0]?.name}</strong>
            </span>
            <Badge variant="success" className="ml-auto">
              {candidates[0]?.billingMode ?? 'unset'}
            </Badge>
          </div>
        )}

        {lookupState === 'done' && ambiguous && (
          <div className="space-y-2 rounded-sm border border-border bg-secondary px-3 py-2">
            <p className="jq-caption text-muted-foreground">{t('matchForm.ambiguous')}</p>
            <ul className="space-y-1.5">
              {candidates.map((candidate) => (
                <li key={candidate.id}>
                  <button
                    type="button"
                    onClick={() => handleSelectCandidate(candidate)}
                    className={
                      'w-full rounded-sm border px-3 py-2 text-left text-[13px] transition-colors' +
                      (selectedCandidateId === candidate.id
                        ? ' border-jq-action-primary bg-background'
                        : ' border-border bg-background/50')
                    }
                  >
                    {candidate.name}
                  </button>
                </li>
              ))}
            </ul>
          </div>
        )}

        {lookupState === 'done' && !matched && candidates.length === 0 && (
          <div className="flex items-center gap-2 rounded-sm border border-border bg-secondary px-3 py-2 text-[13px] text-muted-foreground">
            <UserX className="size-4 shrink-0" strokeWidth={1.5} />
            {t('matchForm.noMatch')}
          </div>
        )}

        {/* ── Ticket type + minimal fields ── */}
        <div className="grid gap-2">
          <Label>{t('matchForm.ticketTypeLabel')}</Label>
          <Tabs value={ticketType} onValueChange={(v) => setTicketType(v as 'sms' | 'qr')}>
            <TabsList className="w-full rounded-sm border border-border bg-secondary p-1">
              <TabsTrigger value="sms" className="flex-1 h-12 text-[14px]">
                SMS
              </TabsTrigger>
              <TabsTrigger value="qr" className="flex-1 h-12 text-[14px]">
                QR
              </TabsTrigger>
            </TabsList>
          </Tabs>
        </div>

        {ticketType === 'sms' && (
          <div className="grid gap-2">
            <Label htmlFor="flash-phone">{t('matchForm.phoneLabel')}</Label>
            <Input
              id="flash-phone"
              type="tel"
              inputMode="tel"
              placeholder={t('matchForm.phonePlaceholder')}
              className="h-12"
              value={phone}
              disabled={disabled}
              onChange={(e) => setPhone(e.target.value)}
            />
          </div>
        )}

        {ticketType === 'qr' && (
          <div className="grid gap-2">
            <Label htmlFor="flash-qrtag">{t('matchForm.qrTagLabel')}</Label>
            <Input
              id="flash-qrtag"
              placeholder={t('matchForm.qrTagPlaceholder')}
              className="h-12"
              value={qrTag}
              disabled={disabled}
              onChange={(e) => setQrTag(e.target.value)}
            />
          </div>
        )}

        <div className="grid grid-cols-2 gap-2">
          <div className="grid gap-2">
            <Label htmlFor="flash-first-name">{t('matchForm.firstNameLabel')}</Label>
            <Input
              id="flash-first-name"
              className="h-12"
              value={firstName}
              disabled={disabled}
              onChange={(e) => setFirstName(e.target.value)}
            />
          </div>
          <div className="grid gap-2">
            <Label htmlFor="flash-last-name">{t('matchForm.lastNameLabel')}</Label>
            <Input
              id="flash-last-name"
              className="h-12"
              value={lastName}
              disabled={disabled}
              onChange={(e) => setLastName(e.target.value)}
            />
          </div>
        </div>

        {zoneField && (
          <div className="grid gap-2">
            <Label htmlFor="flash-parking-zone">
              {zoneField.required ? t('matchForm.zoneLabel') : t('matchForm.zoneLabelOptional')}
            </Label>
            <Input
              id="flash-parking-zone"
              type="text"
              autoComplete="off"
              maxLength={40}
              placeholder={t('matchForm.zonePlaceholder')}
              className="h-12"
              value={parkingZone}
              disabled={disabled}
              onChange={(e) => setParkingZone(e.target.value)}
            />
          </div>
        )}

        {/* ── Angle photo capture (event-photo-capture Slice B) — hidden entirely
            under 'deferred'/undefined photoPolicy; preserves current behavior ── */}
        {showAngleCapture && (
          <section className="space-y-3" aria-label={t('matchForm.vehiclePhotosTitle')}>
            <div>
              <p className="jq-body-sm font-semibold text-foreground">
                {t('matchForm.vehiclePhotosTitle')}
              </p>
              <p className="jq-caption text-muted-foreground">
                {photoPolicy === 'required_4'
                  ? t('matchForm.vehiclePhotosRequired')
                  : t('matchForm.vehiclePhotosOptional')}
              </p>
            </div>

            {photoUpload.activeAngle ? (
              <div className="space-y-2">
                <p className="jq-caption text-muted-foreground">
                  {t('matchForm.capturing')}{' '}
                  <span className="font-medium capitalize text-foreground">
                    {photoUpload.activeAngle}
                  </span>
                </p>
                <CameraCapture
                  angle={photoUpload.activeAngle}
                  onCapture={(blob) => void photoUpload.onCameraCapture(blob)}
                  onCancel={photoUpload.onCameraCancel}
                />
              </div>
            ) : (
              <PhotoCaptureGrid
                slots={photoUpload.slots}
                onSlotCapture={photoUpload.onSlotCapture}
                onSlotRetake={photoUpload.onSlotRetake}
                onAllDoneChange={noop}
              />
            )}
          </section>
        )}

        {/* ── Incident photo capture (event-photo-capture Slice C) — always
            available, independent of photoPolicy/showAngleCapture above ── */}
        <IncidentPhotos key={incidentResetKey} onKeysChange={handleIncidentKeysChange} />

        {formError && (
          <p role="alert" className="text-[13px] text-destructive">
            {formError}
          </p>
        )}

        <Button
          type="button"
          size="touch"
          className="w-full"
          disabled={disabled || !plate.trim() || angleCaptureIncomplete}
          onClick={handleAdd}
        >
          {t('matchForm.addToBatch')}
        </Button>
      </CardContent>
    </Card>
  );
}
